import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getPaddleEnvironment } from "@/lib/paddle";
import { useAuth } from "@/hooks/use-auth";

export type Subscription = {
  id: string;
  paddle_subscription_id: string;
  product_id: string;
  price_id: string;
  status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
};

export function useSubscription() {
  const { user, loading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [credits, setCredits] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = useCallback(async (userId: string) => {
    const { data } = await supabase
      .from("subscriptions")
      .select(
        "id, paddle_subscription_id, product_id, price_id, status, current_period_end, cancel_at_period_end",
      )
      .eq("user_id", userId)
      .eq("environment", getPaddleEnvironment())
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    setSubscription((data as Subscription | null) ?? null);
  }, []);

  const fetchCredits = useCallback(async (userId: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("credits")
      .eq("id", userId)
      .maybeSingle();
    setCredits(((data as { credits?: number } | null)?.credits ?? 0) as number);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setSubscription(null);
      setCredits(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([fetchSubscription(user.id), fetchCredits(user.id)]).finally(() =>
      setLoading(false),
    );

    const suffix = Math.random().toString(36).slice(2);
    const subsChannel = supabase
      .channel(`subscriptions:${user.id}:${suffix}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "subscriptions",
          filter: `user_id=eq.${user.id}`,
        },
        () => fetchSubscription(user.id),
      )
      .subscribe();

    const profilesChannel = supabase
      .channel(`profiles:${user.id}:${suffix}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "profiles",
          filter: `id=eq.${user.id}`,
        },
        () => fetchCredits(user.id),
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subsChannel);
      supabase.removeChannel(profilesChannel);
    };
  }, [user, authLoading, fetchSubscription, fetchCredits]);

  const isActive = (() => {
    if (!subscription) return false;
    const periodOk =
      !subscription.current_period_end || new Date(subscription.current_period_end) > new Date();
    if (["active", "trialing", "past_due"].includes(subscription.status) && periodOk) return true;
    if (subscription.status === "canceled" && periodOk) return true;
    return false;
  })();

  // Under the credit-pack model every authenticated user has access to
  // premium features (history, exports, etc.). Gating is purely credit-based.
  const isPro = !!user;

  return {
    subscription,
    loading: loading || authLoading,
    isActive,
    isPro,
    credits,
  };
}
